/**
 * The standard event taxonomy.
 *
 * These names are the contract PostHog dashboards are built on. Renaming one
 * silently breaks every insight that references it. Add new members. Never
 * repurpose an existing one.
 *
 * Product-specific events keep their own names. This file covers only what
 * instrumented primitives emit on their own (buttons, views, requests), so one
 * insight holds across the landing site, the desktop app and the gateway.
 */
import type { AnalyticsProperties } from './types'

export const ANALYTICS_EVENTS = {
  /**
   * A named interactive control was activated.
   *
   * One event for every control, discriminated by `element_type`, rather than
   * one event per kind. A control that changes from a button to a link keeps
   * its insight, and "what gets used most" stays a single breakdown instead of
   * a union the author has to already know the members of. The verb is absent
   * on purpose: the same control is activated by mouse and by hotkey, and
   * `trigger` says which.
   */
  UI_INTERACTION: 'ui_interaction',
  /** A view became visible: a page, panel, modal, popover, sheet or menu. */
  UI_VIEW_SHOWN: 'ui_view_shown',
  /** A view was dismissed. */
  UI_VIEW_HIDDEN: 'ui_view_hidden',

  /** An outbound request was issued. */
  REQUEST_STARTED: 'request_started',
  /** An outbound request returned a success status. */
  REQUEST_SUCCEEDED: 'request_succeeded',
  /** An outbound request threw, timed out, or returned a failure status. */
  REQUEST_FAILED: 'request_failed',

  /** An account was signed into on this device. */
  USER_SIGNED_IN: 'user_signed_in',
  /** An account was signed out of. Captured before the reset, so it lands on that account. */
  USER_SIGNED_OUT: 'user_signed_out',

  /** An unhandled error reached an error boundary. */
  ERROR_BOUNDARY_TRIGGERED: 'error_boundary_triggered',
} as const

export type AnalyticsEventName = (typeof ANALYTICS_EVENTS)[keyof typeof ANALYTICS_EVENTS]

/**
 * What kind of view `UI_VIEW_SHOWN` is reporting.
 *
 * Named for the sidepanel's view registry, whose `view.id` and `data-view-id`
 * drop in as `view_name` with no translation. That registry is the only place
 * in the app that already names navigation surfaces, so matching it keeps the
 * analytic name and the deeplinkable id from drifting apart.
 */
export type ViewKind = 'page' | 'panel' | 'modal' | 'popover' | 'sheet' | 'drawer' | 'menu' | 'tooltip' | 'toast'

/** What kind of control was activated. */
export type ElementKind = 'button' | 'link' | 'menu_item' | 'tab' | 'checkbox' | 'toggle' | 'cell' | 'row'

/** How it was activated. A control with a hotkey answers to both. */
export type InteractionTrigger = 'click' | 'keyboard' | 'hotkey'

/**
 * Extends the open property bag so a call site can attach context the named
 * fields don't cover — which item a row acted on, where a modal was opened
 * from. Stating it here beats intersecting at every signature that takes one,
 * which is how the ability got dropped in a rename once already.
 */
export interface InteractionProperties extends AnalyticsProperties {
  /**
   * Stable, human-readable id, unique within its view. The axis dashboards
   * break down on, and the only property that is not optional.
   *
   * Optional only during the naming backlog: instrumented primitives report
   * every interaction, so `ui_interaction where element_name is null` measures
   * what is left to name. The enforcement change makes the primitive's *prop*
   * required, which is what actually closes the gap.
   */
  element_name?: string
  /** Not recoverable after the fact, which is why it is a property and not part of the name. */
  element_type?: ElementKind
  /** Defaults to `click`; set it when the activation was not a pointer. */
  trigger?: InteractionTrigger
  /** The view the control was in, when the provider can supply one. */
  view_name?: string
  /** Set when the activation is the primary conversion action in the view. */
  is_primary_action?: boolean
  /**
   * Visible label of an as-yet-unnamed control, to seed the audit. Never a
   * substitute for `element_name`: copy changes without anyone thinking about
   * analytics, so a dashboard built on this would rot silently.
   */
  element_label_hint?: string
}

export interface ViewShownProperties extends AnalyticsProperties {
  view_name: string
  view_kind: ViewKind
  /** The view that opened this one, for modals and popovers. */
  parent_view_name?: string
}

export interface RequestProperties extends AnalyticsProperties {
  /** Route template, never the interpolated path. `/v1/chat/:id`, not `/v1/chat/abc`. */
  request_name: string
  request_method?: string
  request_status?: number
  duration_ms?: number
  /** Present on REQUEST_FAILED only. */
  error_kind?: string
}
